import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Sparkles, X, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const Card = ({ i, value, chosen, flipped, onPick, disabled }) => (
  <motion.button type="button" onClick={() => onPick(i)} disabled={disabled} data-testid={`cardgame-card-${i}`}
    initial={{ y: 40, opacity: 0, rotate: (i - 1) * 6 }} animate={{ y: 0, opacity: 1, rotate: flipped ? 0 : (i - 1) * 6 }}
    whileHover={disabled ? {} : { y: -12, rotate: 0, scale: 1.05 }} transition={{ delay: i * 0.12, type: "spring", stiffness: 220, damping: 16 }}
    className="relative w-24 h-36 sm:w-32 sm:h-48 [perspective:900px] disabled:cursor-default">
    <motion.div className="absolute inset-0 [transform-style:preserve-3d]" animate={{ rotateY: flipped ? 180 : 0 }}
      transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}>
      <div className="absolute inset-0 [backface-visibility:hidden] rounded-2xl border-2 border-[#3ad7ff]/60 bg-gradient-to-br from-[#0d1a3a] to-[#1a0d33] grid place-items-center shadow-[0_0_30px_rgba(58,215,255,0.25)] overflow-hidden">
        <div className="absolute inset-2 rounded-xl border border-white/10" />
        <motion.img src="/logo-x.png" alt="CLIP-EX" className="w-14 sm:w-20" animate={{ rotate: [0, -8, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, delay: i * 0.3 }} />
        <span className="absolute bottom-3 font-display text-[10px] tracking-[0.3em] text-[#3ad7ff]">CLIP-EX</span>
      </div>
      <div className={`absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl border-2 grid place-items-center ${chosen ? "border-[#ffc94d] bg-gradient-to-br from-[#3a2a00] to-[#1a1000] shadow-[0_0_50px_rgba(255,201,77,0.6)]" : "border-white/10 bg-[#0b0f1c] opacity-60"}`}>
        <div className="text-center">
          <div className={`font-display text-4xl sm:text-5xl ${chosen ? "text-[#ffc94d]" : "text-slate-400"}`} data-testid={`cardgame-value-${i}`}>{value ?? "?"}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-300 mt-1">clips</div>
        </div>
      </div>
    </motion.div>
  </motion.button>
);

const ShareWin = ({ clips, code, t }) => {
  const url = window.location.origin;
  const text = t("cg.share_text").replace("{n}", clips).replace("{code}", code);
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ text, url }); } catch { /* cancelled */ } return; }
    await navigator.clipboard?.writeText(`${text} ${url}`);
    toast.success(t("cg.copied"));
  };
  const enc = encodeURIComponent;
  return (
    <div className="flex items-center gap-2">
      <button onClick={share} data-testid="cardgame-share-btn" className="cx-metal rounded-full px-5 py-3 text-sm font-semibold flex items-center gap-2"><Share2 className="w-4 h-4" />{t("cg.share")}</button>
      <a href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="cardgame-share-x" className="cx-metal rounded-full w-11 h-11 grid place-items-center text-sm font-bold">𝕏</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`} target="_blank" rel="noreferrer" data-testid="cardgame-share-fb" className="cx-metal rounded-full w-11 h-11 grid place-items-center text-sm font-bold">f</a>
    </div>
  );
};

export const CardGame = ({ game, onClose, onWon }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [all, setAll] = useState(false);
  const n = game.prizes.length;
  const pick = async (i) => {
    if (busy || res) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: game.code, pick: i });
      setRes({ ...data, pick: i });
      setTimeout(() => setAll(true), 1300);
      onWon?.(data);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="cardgame-modal">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative cx-card rounded-3xl p-6 sm:p-10 max-w-2xl w-full text-center">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="cardgame-close-btn"><X className="w-5 h-5" /></button>
        <div className="text-xs font-mono tracking-[0.3em] text-[#3ad7ff]">{game.code}</div>
        <h2 className="font-display text-2xl sm:text-3xl text-white mt-2">{res ? t("cg.won").replace("{n}", res.clips) : t("cg.title")}</h2>
        <p className="text-sm text-slate-400 mt-2">{res ? t("cg.won_sub") : t("cg.sub").replace("{p}", game.prizes.join(", "))}</p>
        <div className="flex justify-center gap-3 sm:gap-6 mt-8">
          {Array.from({ length: n }, (_, i) => (
            <Card key={i} i={i} onPick={pick} disabled={busy || !!res} chosen={res?.pick === i}
              flipped={!!res && (res.pick === i || all)} value={res?.reveal?.[i]} />
          ))}
        </div>
        <div className="min-h-14 mt-6 flex flex-wrap justify-center items-center gap-3">
          {busy && <Loader2 className="w-5 h-5 animate-spin text-[#3ad7ff]" />}
          <AnimatePresence>{all && (
            <motion.button initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={onClose} data-testid="cardgame-done-btn"
              className="cx-btn rounded-full px-8 py-3 text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4" />{t("cg.done")}</motion.button>
          )}</AnimatePresence>
          {all && <ShareWin clips={res.clips} code={game.code} t={t} />}
        </div>
      </motion.div>
    </div>
  );
};
